<template>
  <div>
    <q-btn-group spread class="q-mb-md">
      <q-btn color="secondary" label="Save" @click="saveColumns"> </q-btn>
      <q-btn color="secondary" label="Reset...">
        <q-menu auto-close fit>
          <q-list style="min-width: 100px">
            <q-item clickable @click="resetToSavedColumns">
              <q-item-section>Reset to saved columns</q-item-section>
            </q-item>
            <q-separator />
            <q-item clickable @click="resetToDefaultColumns">
              <q-item-section>Reset to default columns</q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>
    </q-btn-group>
    <div>
      <Banner v-if="bannerVisible" :mode="bannerMode">
        {{ bannerMessage }}
      </Banner>
    </div>
    <q-expansion-item
      expand-separator
      icon="view_column"
      group="lcvm"
      v-for="(category, index) in listViewColumnsCategories"
      :key="index"
      :dense="denseListView"
      xx-default-opened="category === 'Course Info'"
      :class="{
        'bg-light-blue-1': selectionDetails[category].count > 0,
      }"
    >
      <template v-slot:header>
        <q-item-section>
          <q-item-label>{{ category }}</q-item-label>
          <q-item-label caption
            >{{ selectionDetails[category].count }} of
            {{ selectionDetails[category].allIds.length }}
            selected</q-item-label
          >
        </q-item-section>
        <q-item-section side>
          <q-toggle
            :value="selectionDetails[category].selected"
            :val="category"
            @input="toggleCategory(category)"
            :dense="denseListView"
            color="primary"
            keep-color
          />
        </q-item-section>
      </template>
      <q-card>
        <q-card-section>
          <q-item
            tag="label"
            v-for="dp in listViewColumnsGroupedByCategory[category]"
            :key="dp.name"
            :dense="denseListView"
          >
            <q-item-section>
              <q-item-label v-if="dp.labelLVCM">{{
                dp.labelLVCM
              }}</q-item-label>
              <q-item-label v-else>{{ dp.label }}</q-item-label>
              <q-item-label caption>{{ dp.description }}</q-item-label>
            </q-item-section>
            <q-item-section avatar>
              <q-toggle
                v-model="selectedItems"
                :val="dp.name"
                :dense="denseListView"
              />
            </q-item-section>
          </q-item>
        </q-card-section>
      </q-card>
    </q-expansion-item>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { xor } from "lodash";
import Banner from "@/components/Banner";
import { DEFAULT_FILTER_COLUMNS } from "@/constants";

export default {
  name: "ListViewColumnsManager",
  components: {
    Banner,
  },
  data() {
    return {
      bannerVisible: false,
      bannerDismissDelay: 7000,
      bannerMode: "positive",
      bannerMessage: "",
      selectedCategories: [],
      selectedItems: [],
    };
  },
  mounted() {
    this.selectedItems = [...this.filterColumns];
  },
  computed: {
    ...mapGetters([
      "listViewColumnsConfig",
      "listViewColumnsGroupedByCategory",
      "listViewColumnsCategories",
      "savedColumns",
    ]),
    ...mapState(["denseListView", "filterColumns"]),
    // contains all the meta we need to drive our UI
    selectionDetails() {
      const details = {};
      const selectedItems = this.selectedItems;
      const categories = this.listViewColumnsCategories;
      const itemsByCat = this.listViewColumnsGroupedByCategory;

      categories.forEach(category => {
        // get all the ids for the current category
        const allIds = itemsByCat[category].map(datapoint => datapoint.name);

        // if 'all' is true, all the ids for the current category are selected
        const all = allIds.every(datapointId =>
          selectedItems.includes(datapointId),
        );
        // if 'none' is true, none of the ids for the current category are selected
        const none = allIds.every(
          datapointId => !selectedItems.includes(datapointId),
        );
        // if 'some' is true, some of the ids for the current category are selected
        const some =
          !all &&
          allIds.some(datapointId => selectedItems.includes(datapointId));
        // we use the count for display purposes
        const count = selectedItems.filter(datapointId =>
          allIds.includes(datapointId),
        ).length;

        // we use 'selected' as our v-model for the current category
        // setting it to 'null' achieves the indeterminate state in the q-toggle
        let selected = false;

        if (all) {
          selected = true;
        } else if (some) {
          selected = null;
        }

        // attach a new 'details' property with our summary info for each category
        details[category] = {
          allIds,
          all,
          none,
          some,
          selected,
          count,
        };
      });

      return details;
    },
  },
  methods: {
    ...mapActions(["updateSavedColumns"]),
    toggleCategory(categoryId) {
      console.debug("toggleCategory", categoryId);

      this.selectedCategories = xor(this.selectedCategories, [categoryId]);

      const allIds = this.selectionDetails[categoryId].allIds;

      if (this.selectedCategories.includes(categoryId)) {
        console.debug("should add datapoints", allIds);
        allIds.forEach(id => {
          if (!this.selectedItems.includes(id)) {
            this.selectedItems.push(id);
          }
        });
      } else {
        console.debug("should remove datapoints", allIds);
        this.selectedItems = this.selectedItems.filter(
          item => !allIds.includes(item),
        );
      }
    },
    saveColumns() {
      const cols = [...this.filterColumns];

      this.updateSavedColumns(cols).then(() => {
        this.showBanner(
          "Your default columns have been saved.",
          "positive",
          this.bannerDismissDelay,
        );
      });
    },
    resetToSavedColumns() {
      const cols = [...this.savedColumns];

      this.selectedItems = cols;
      this.$store.commit("setFilterColumns", cols);
      this.showBanner(
        "Columns have been reset to your last saved defaults.",
        "info",
        this.bannerDismissDelay,
      );
    },
    resetToDefaultColumns() {
      const cols = [...DEFAULT_FILTER_COLUMNS];

      this.selectedItems = cols;
      this.$store.commit("setFilterColumns", cols);
      this.showBanner(
        "Columns have been reset to the Open Links defaults.",
        "info",
        this.bannerDismissDelay,
      );
    },
    showBanner(message, mode, dismissDelay = this.bannerDismissDelay) {
      this.bannerVisible = true;
      this.bannerMode = mode;
      this.bannerMessage = message;

      setTimeout(() => {
        this.bannerVisible = false;
      }, dismissDelay);
    },
  },
  watch: {
    selectionDetails(newVal) {
      for (const [key, value] of Object.entries(newVal)) {
        if (value.all && !this.selectedCategories.includes(key)) {
          this.selectedCategories.push(key);
        } else if (value.none && this.selectedCategories.includes(key)) {
          this.selectedCategories = xor(this.selectedCategories, [key]);
        }
      }

      this.$store.commit("setFilterColumns", this.selectedItems);
    },
  },
};
</script>
<style scoped></style>
